/**
 * PATH: packages/ai-hub/src/hub/components/FileUploadModal.tsx
 * FILE_ID: AIHUB-PKG-UPLOAD-MODAL-001
 * PURPOSE: Modal for uploading files to a job folder from AI Hub
 * WHY: Users need a UI to select a file, pick a job, and upload to QDrive
 * FLOW:
 * ┌──────────┐  ┌──────────────┐  ┌───────────┐  ┌─────────┐
 * │ Pick File │ → │ Select Job   │ → │ Upload    │ → │ Success │
 * └──────────┘  └──────────────┘  └───────────┘  └─────────┘
 * DEPENDENCIES:
 * - upload-service: uploadFileToJob, fetchJobsList
 * - lucide-react: icons
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  X, Upload, FileUp, Check, AlertCircle, Search, Loader2, File as FileIcon,
} from 'lucide-react';
import { cn } from '../lib/utils';
import {
  uploadFileToJob, fetchJobsList,
  type UploadProgress, type UploadResult, type JobOption,
} from '../services/upload-service';

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: (result: UploadResult, job: JobOption) => void;
}

export function FileUploadModal({ isOpen, onClose, onUploadComplete }: FileUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobOption | null>(null);
  const [jobSearch, setJobSearch] = useState('');
  const [jobs, setJobs] = useState<JobOption[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [progress, setProgress] = useState<UploadProgress>({ loaded: 0, total: 0, percent: 0 });
  const [errorMessage, setErrorMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const resetState = useCallback(() => {
    setFile(null);
    setSelectedJob(null);
    setJobSearch('');
    setUploadState('idle');
    setProgress({ loaded: 0, total: 0, percent: 0 });
    setErrorMessage('');
    setShowJobDropdown(false);
  }, []);

  const handleClose = useCallback(() => {
    if (uploadState === 'uploading') return;
    resetState();
    onClose();
  }, [uploadState, resetState, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setIsLoadingJobs(true);
    fetchJobsList()
      .then(setJobs)
      .catch(() => setJobs([]))
      .finally(() => setIsLoadingJobs(false));
  }, [isOpen]);

  useEffect(() => {
    if (!jobSearch.trim()) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setIsLoadingJobs(true);
      fetchJobsList(jobSearch)
        .then(setJobs)
        .catch(() => {})
        .finally(() => setIsLoadingJobs(false));
    }, 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [jobSearch]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowJobDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    setUploadState('idle');
    setErrorMessage('');
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFileSelect(dropped);
  }, [handleFileSelect]);

  const handleUpload = useCallback(async () => {
    if (!file || !selectedJob) return;
    setUploadState('uploading');
    setErrorMessage('');
    try {
      const result = await uploadFileToJob(file, selectedJob.id, setProgress);
      setUploadState('success');
      onUploadComplete(result, selectedJob);
    } catch (err) {
      setUploadState('error');
      setErrorMessage(err instanceof Error ? err.message : 'Upload failed');
    }
  }, [file, selectedJob, onUploadComplete]);

  if (!isOpen) return null;

  const filteredJobs = jobSearch.trim()
    ? jobs.filter((j) => {
        const q = jobSearch.toLowerCase();
        return j.jobNumber.toLowerCase().includes(q) ||
               j.jobName.toLowerCase().includes(q) ||
               j.clientName.toLowerCase().includes(q);
      })
    : jobs;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-card rounded-2xl border border-border shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <Upload className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">Upload File</h2>
              <p className="text-xs text-muted-foreground">Upload to a job folder in QDrive</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {uploadState === 'success' ? (
            <div className="text-center py-6 space-y-3">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Check className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="text-sm font-semibold text-foreground">Upload Complete</p>
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">{file?.name}</span> uploaded to Job #{selectedJob?.jobNumber}
              </p>
              <button onClick={handleClose} className="mt-3 px-6 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Drop Zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all",
                  isDragging ? "border-primary bg-primary/5 scale-[1.01]" : "border-border hover:border-primary/40 hover:bg-accent/30",
                  file ? "bg-accent/20" : "",
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
                />
                {file ? (
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-primary/10 flex-shrink-0">
                      <FileIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-left min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null); setUploadState('idle'); }}
                      className="p-1 rounded-lg hover:bg-accent transition-colors flex-shrink-0"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <FileUp className="w-10 h-10 text-muted-foreground/50 mx-auto" />
                    <p className="text-sm font-medium text-foreground">Drop a file here or click to browse</p>
                    <p className="text-xs text-muted-foreground">All file types accepted</p>
                  </div>
                )}
              </div>

              {/* Job Selector */}
              <div ref={dropdownRef} className="relative">
                <label className="block text-xs font-medium text-foreground mb-1.5">Destination Job</label>
                <div
                  onClick={() => setShowJobDropdown(true)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2.5 border rounded-xl cursor-pointer transition-colors",
                    showJobDropdown ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/40",
                  )}
                >
                  <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  {selectedJob && !showJobDropdown ? (
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-foreground">
                        #{selectedJob.jobNumber} — {selectedJob.jobName}
                      </span>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={jobSearch}
                      onChange={(e) => { setJobSearch(e.target.value); setShowJobDropdown(true); }}
                      onFocus={() => setShowJobDropdown(true)}
                      placeholder="Search by job number, name, or client..."
                      className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
                    />
                  )}
                  {isLoadingJobs && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin flex-shrink-0" />}
                </div>

                {showJobDropdown && (
                  <div className="absolute z-10 w-full mt-1.5 bg-card border border-border rounded-xl shadow-xl max-h-52 overflow-y-auto">
                    {filteredJobs.length === 0 ? (
                      <div className="p-4 text-center text-xs text-muted-foreground">
                        {isLoadingJobs ? 'Loading jobs...' : 'No jobs found'}
                      </div>
                    ) : (
                      filteredJobs.slice(0, 50).map((job) => (
                        <button
                          key={job.id}
                          onClick={() => { setSelectedJob(job); setJobSearch(''); setShowJobDropdown(false); }}
                          className={cn(
                            "w-full text-left px-3 py-2.5 hover:bg-accent transition-colors border-b border-border last:border-b-0",
                            selectedJob?.id === job.id && "bg-primary/5",
                          )}
                        >
                          <p className="text-sm font-medium text-foreground">
                            #{job.jobNumber} — {job.jobName}
                          </p>
                          {job.clientName && (
                            <p className="text-xs text-muted-foreground mt-0.5">{job.clientName}</p>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Progress Bar */}
              {uploadState === 'uploading' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Uploading...</span>
                    <span>{progress.percent}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-accent overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-300"
                      style={{ width: `${progress.percent}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Error */}
              {uploadState === 'error' && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-destructive/10 border border-destructive/20">
                  <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-destructive">{errorMessage}</p>
                    <button onClick={handleUpload} className="text-xs text-destructive underline mt-1">
                      Try again
                    </button>
                  </div>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={!file || !selectedJob || uploadState === 'uploading'}
                className={cn(
                  "w-full py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2",
                  !file || !selectedJob || uploadState === 'uploading'
                    ? "bg-accent text-muted-foreground cursor-not-allowed"
                    : "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20",
                )}
              >
                {uploadState === 'uploading' ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4" /> Upload to Job #{selectedJob?.jobNumber || '...'}</>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
